import { Alert, AlertTitle } from "@mui/material";
import * as React from "react";
import { useSearchParams } from "react-router";

import { PageLayout } from "@/shared/components/PageLayout";
import {
  EphemeralWorkspaceState,
  EphemeralWorkspaceStateContext,
} from "@/shared/components/visualization/WorkspaceCtx";
import { LayoutRecord, LayoutService } from "@/shared/domain/layouts";
import { getBrowser, SearchParams } from "@/shared/environment";
import { usePopupState } from "@/shared/hooks";
import {
  CURRENT_TIME_NS_FIELD,
  registerLiveVisualizerState,
  useRegisterViewingContext,
} from "@/shared/services";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { useVizState } from "@/shared/state/visualization";
import { nanoSecToSecStr, strSecToBigIntNanosec } from "@/shared/time";

import { DndContainer } from "../DndContainer";
import { Header } from "../Header";
import { DatasourceInfo } from "../Header/DatasourceInfo";
import { LAYOUT_CONFLICT_ERROR_NAME, useWorkspaceLoaderData } from "../loader";
import { PanelBoard } from "../PanelBoard";
import { Sidebar } from "../Sidebar";
import { Timeline } from "../Timeline";
import { sessionNotices, useWorkspaceState } from "../WorkspaceCtx";

import { useEnforceStorageQuota } from "./hooks/useEnforceStorageQuota";
import { useEventDeepLink } from "./hooks/useEventDeepLink";
import { useLayoutHydration } from "./hooks/useLayoutHydration";
import { useSuggestChromium } from "./hooks/useSuggestChromium";
import { useWorkspaceActionSubscriber } from "./hooks/useWorkspaceActionSubscriber";
import { LayoutConflictModal } from "./LayoutConflictModal";
import { type Notification, Notifications } from "./Notifications";
import { singleOrgIdForDataSources } from "./singleOrgIdForDataSources";
import styles from "./Workspace.module.css";
import {
  initialNotifications,
  notificationFor,
  withNewNotifications,
} from "./workspaceNotifications";

export function Workspace() {
  useEnforceStorageQuota();

  useEventDeepLink();

  const visualizerInitData = useWorkspaceLoaderData();
  const workspaceContext = useWorkspaceState();

  const vizState = useVizState();

  useWorkspaceActionSubscriber(workspaceContext);

  const dataSources = workspaceContext.files.map((file) => {
    return {
      type: "file" as const,
      id: file.file_id,
      record: file,
    };
  });

  useRegisterViewingContext("visualizer", {
    dataSources,
    state: vizState as unknown as Record<string, unknown>,
    // The visualizer is the only multi-resource view, so it resolves its own
    // cross-org ambiguity here: one org when every source agrees, else none.
    orgId: singleOrgIdForDataSources(dataSources),
  });

  // The playhead advances at tick rate during playback, so it bypasses the
  // reactive viewing-context registration above and is instead resolved
  // imperatively when a reader (the AI chat send path) asks for it. The
  // timer is a stable, internally-mutable object for the lifetime of the
  // workspace, so this closure never goes stale and the effect registers
  // exactly once per mount.
  const { timer } = workspaceContext;
  React.useEffect(
    function registerLivePlayhead() {
      return registerLiveVisualizerState(() => {
        // Timer.currentTime yields 0n when no time span contains the current
        // position — its "no data" sentinel, never a real recording
        // timestamp. Contribute nothing in that case.
        const currentTimeNs = timer.currentTime;
        return currentTimeNs === 0n
          ? {}
          : { [CURRENT_TIME_NS_FIELD]: currentTimeNs.toString() };
      });
    },
    [timer],
  );

  const [workspaceNotifications, setWorkspaceNotifications] = React.useState<
    Notification[]
  >(() => initialNotifications(visualizerInitData));

  const layoutConflictModalPopup = usePopupState<{
    userLayout: LayoutRecord;
    sharedLayout: LayoutRecord;
  }>();

  useSyncTimerAndSearchParams(workspaceContext);

  const { error: layoutHydrationError } = useLayoutHydration(workspaceContext);

  React.useEffect(
    function reportHydrationError() {
      if (!layoutHydrationError) {
        return;
      }

      const notification: Notification = {
        id: layoutHydrationError.id,
        message: layoutHydrationError.message,
        severity: "error",
      };
      setWorkspaceNotifications((prevNotifications) =>
        withNewNotifications(prevNotifications, [notification]),
      );
    },
    [layoutHydrationError],
  );

  const { sessions, sessionLoadFailures } = workspaceContext;
  React.useEffect(
    function reportSessionOutcomes() {
      const sessionNotifications = sessionNotices(
        sessions,
        sessionLoadFailures,
      ).map(notificationFor);
      setWorkspaceNotifications((prevNotifications) =>
        withNewNotifications(prevNotifications, sessionNotifications),
      );
    },
    [sessions, sessionLoadFailures],
  );

  const openLayoutConflictModal = layoutConflictModalPopup.open;
  React.useEffect(
    function handleLayoutConflictError() {
      const configSource = visualizerInitData.configSource;
      if (
        configSource?.type === "recoverable-error" &&
        configSource.value.name === LAYOUT_CONFLICT_ERROR_NAME
      ) {
        try {
          const { userLayout, sharedLayout } =
            LayoutService.parseRobotoLayoutConflictException(
              configSource.value.metadata?.conflictingLayouts,
            );

          openLayoutConflictModal({
            metadata: { userLayout, sharedLayout },
          });
        } catch (e) {
          // Swallow and report the programming error
          ErrorMonitoringService.captureError(e);
        }
      }
    },
    [openLayoutConflictModal, visualizerInitData],
  );

  const { showChromiumSuggestion, hideChromiumSuggestion } =
    useSuggestChromium();
  const browser = getBrowser();

  return (
    <EphemeralWorkspaceStateContext.Provider value={workspaceContext}>
      <PageLayout
        title={"Roboto - Visualization"}
        headerContentLeft={<DatasourceInfo />}
        headerContentRight={<Header />}
      >
        <div className={styles.workspaceContainer}>
          {showChromiumSuggestion && (
            <Alert
              severity="warning"
              onClose={hideChromiumSuggestion}
              className={styles.chromiumSuggestionAlert}
            >
              <AlertTitle>
                Roboto recommends Chrome for data visualization.
              </AlertTitle>
              <p>
                We&apos;ve measured best performance on Chrome and other
                Chromium-based browsers for loading large amounts of time series
                data in Roboto.{" "}
                {browser === "Mozilla Firefox" && (
                  <>
                    The Mozilla team is working to improve Firefox performance (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://bugzilla.mozilla.org/show_bug.cgi?id=1800713"
                    >
                      link
                    </a>
                    ).{" "}
                  </>
                )}
              </p>
              <p>
                Consider switching to a Chromium-based browser if the data load
                is interfering with your workflow.
              </p>
            </Alert>
          )}
          <Notifications
            onDismiss={(notification) => {
              setWorkspaceNotifications((prevNotifications) =>
                prevNotifications.filter((n) => n.id !== notification.id),
              );
            }}
            notifications={workspaceNotifications}
          />
          <DndContainer>
            <div className={styles.primaryContent}>
              <Sidebar />
              <PanelBoard
                className={styles.panelBoard}
                errorMsg={workspaceContext.error?.message}
              />
            </div>
          </DndContainer>
          <Timeline />
        </div>

        <LayoutConflictModal
          open={layoutConflictModalPopup.isOpen}
          conflictingLayouts={layoutConflictModalPopup.metadata}
          onClose={layoutConflictModalPopup.close}
          onLayoutRenamed={workspaceContext.setActiveLayout}
        />
      </PageLayout>
    </EphemeralWorkspaceStateContext.Provider>
  );
}

/**
 * Synchronizes the given {@param timer} to the URL query string.
 *
 * This works like a database and a cache -- the query parameters are the source of truth
 * for what time to jump the workspace to, and the timer contains a cache where the next
 * state for the time value is stored.
 *
 * When the timer stops or seeks, it commits the working cache to the URL search params.
 *
 * If the value from the URL search params changes, it invalidates the working cache.
 */
function useSyncTimerAndSearchParams({ timer }: EphemeralWorkspaceState) {
  const expectedTimeSearchParam = React.useRef<string>();
  const [searchParams, setSearchParams] = useSearchParams();
  React.useEffect(
    function onSyncTimerAndSearchParams() {
      function syncTimerToParams() {
        // Sync the query params to the timer
        const timestamp = searchParams.get(SearchParams.TIMESTAMP) ?? undefined;
        // Only sync timer state after the timer has data.
        if (timer.duration <= 0) {
          return;
        }

        // Sync the timer to query params when and only when the
        // query params have been updated independently of the timer.
        if (
          timestamp !== undefined &&
          // Don't apply this update if it has already been applied.
          timestamp !== expectedTimeSearchParam.current
        ) {
          timer.seekTo(strSecToBigIntNanosec(timestamp));
          expectedTimeSearchParam.current = timestamp;
        }
      }

      function syncParamsToTimer() {
        // Sync the query params to the timer
        const timestamp = nanoSecToSecStr(timer.currentTime);
        // Only sync timer state after the timer has data.
        if (timer.duration <= 0) {
          return;
        }

        // Short-circuit the feedback loop from the timer to the query parameters.
        expectedTimeSearchParam.current = timestamp;
        // Avoid an unnecessary params update if possible.
        if (searchParams.get(SearchParams.TIMESTAMP) !== timestamp) {
          setSearchParams(
            (oldParams) => {
              const params = new URLSearchParams(oldParams);
              params.set(SearchParams.TIMESTAMP, timestamp);
              return params;
            },
            { preventScrollReset: true, replace: true },
          );
        }
      }
      // If the query params or timer changed, then sync them together now.
      syncTimerToParams();

      // Sync again when the timer's internal time span state changes.
      const abortController = new AbortController();
      const listenerOptions = { signal: abortController.signal };
      // timeSpanUpdate means that the timer's duration and start time may have both changed.
      timer.addListener("timeSpanUpdate", syncTimerToParams, listenerOptions);

      // When the timer stops on a specific time, sync the query params to the timer.
      timer.addListener("stop", syncParamsToTimer, listenerOptions);
      timer.addListener("seek", syncParamsToTimer, listenerOptions);
      return function disposeOnSyncTimer() {
        abortController.abort();
      };
    },
    [timer, setSearchParams, searchParams],
  );
}
